import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { PlainButton, SkeletonBar, inkA, orange, tokens } from 'components/v3';
import { barPercent, chartScale } from './chartScale';

// "Entries over time" (Implementation List §6): 14 weekly bars, each a
// rolling 7-day window ending now (T30), oldest first, with the current
// week highlighted. The header reads "[n] entries in the last 14 weeks ·
// avg [x]/week" with the date range on the right. The y axis rounds up to
// a step of 2, 5, 10, 25 or 50 (chartScale). Under the bars each week's
// start date, with the month only on a month's first week (and on phones,
// where every other label is dropped). Hover, focus or tap a bar for its
// dates and count: the app-wide tooltip reads the bar's data-tip
// attributes, and the bar's accessible name says the same.
//
// Generalised for the W-9 dashboard (w9-plan §6.3 item 5) with optional
// `buckets` + `labels`: `buckets` is a list of counts, `labels` their
// x-axis captions (same length), `dimFrom` the first index drawn muted
// (future months), `currentIndex` the highlighted bar (default: the last
// one not dimmed), `title` / `summary` / `range` / `note` the header copy.
// Without `buckets` the chart is today's weekly one, unchanged.

const PLOT_HEIGHT = 146;
// Room above the tallest bar for its count.
const LABEL_BAND = 14;
const AREA = PLOT_HEIGHT - LABEL_BAND;
const AXIS_WIDTH = 24;
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const MINUTE = 60000;

const toDate = (iso) => new Date(iso);
const monthDay = (d) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;

// A week's last moment: windows end where the next one starts, so step back
// a minute to land on the right day.
const weekEnd = (week) => new Date(toDate(week.week_end).getTime() - MINUTE);

export const weekRange = (week) =>
  `${monthDay(toDate(week.week_start))} – ${monthDay(weekEnd(week))}`;

export const chartRange = (weekly) => {
  if (!weekly || !weekly.length) return '';
  const last = weekEnd(weekly[weekly.length - 1]);
  return `${monthDay(toDate(weekly[0].week_start))} – ${monthDay(
    last
  )}, ${last.getFullYear()}`;
};

const entries = (n) =>
  `${Number(n || 0).toLocaleString()} ${n === 1 ? 'entry' : 'entries'}`;

// "Jun 3 – Jun 9: 4 entries" (+ "(last 7 days)" on the current week).
export const barTip = (week, isCurrent) => ({
  title: `${weekRange(week)}${isCurrent ? ' (last 7 days)' : ''}`,
  body: entries(week.count),
});

// x labels: the week's start day, with the month on a month's first week
// and on the current week.
export const axisLabels = (weekly) =>
  weekly.map((week, i) => {
    const start = toDate(week.week_start);
    const prev = i > 0 ? toDate(weekly[i - 1].week_start) : null;
    const current = i === weekly.length - 1;
    return {
      day: start.getDate(),
      month: MONTHS[start.getMonth()],
      monthStart: !prev || prev.getMonth() !== start.getMonth(),
      current,
      // phones keep every other label, counting back from this week
      alt: (weekly.length - 1 - i) % 2 === 1,
    };
  });

const Bar = styled(PlainButton)`
  position: relative;
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  cursor: default;

  &:focus-visible {
    box-shadow: 0 0 0 2px ${orange(0.55)};
    border-radius: 4px;
  }

  .bar {
    width: 100%;
    max-width: 30px;
    min-height: 2px;
    border-radius: 4px 4px 0 0;
    opacity: ${(p) => (p.$current ? 1 : 0.6)};
    transition: opacity 120ms ease;
  }

  &:hover .bar,
  &:focus-visible .bar {
    opacity: 1;
  }

  .val {
    margin-bottom: 3px;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  &:hover .val {
    color: ${orange(1)};
  }
`;

// The weekly series as generic bars: { key, count, tipTitle, tipBody,
// caption, captionTone, captionWeight, hidden, current, dim }.
const weeklyBars = (weekly, isMobile) => {
  const labels = axisLabels(weekly);
  return weekly.map((week, i) => {
    const current = i === weekly.length - 1;
    const tip = barTip(week, current);
    const label = labels[i];
    const showMonth = isMobile || label.monthStart || label.current;
    let tone = 'faint';
    if (label.current) tone = 'primary';
    else if (label.monthStart) tone = 'sub';
    let weight = 600;
    if (label.current) weight = 800;
    else if (label.monthStart) weight = 700;
    return {
      key: week.week_start,
      count: week.count,
      tipTitle: tip.title,
      tipBody: tip.body,
      caption: `${showMonth ? `${label.month} ` : ''}${label.day}`,
      captionTone: tone,
      captionWeight: weight,
      hidden: isMobile && label.alt,
      current,
      dim: false,
    };
  });
};

// Generic buckets (w9-plan §6.3 item 5) as bars.
export const bucketBars = (
  buckets,
  labels,
  { dimFrom = null, currentIndex = null } = {}
) => {
  const counts = (buckets || []).map((n) => Number(n) || 0);
  const dimAt = dimFrom == null ? counts.length : dimFrom;
  const current =
    currentIndex == null ? Math.min(dimAt, counts.length) - 1 : currentIndex;
  return counts.map((count, i) => {
    const caption = (labels && labels[i]) || String(i + 1);
    return {
      key: `${i}-${caption}`,
      count,
      tipTitle: caption,
      tipBody: entries(count),
      caption,
      captionTone: i === current ? 'primary' : 'sub',
      captionWeight: i === current ? 800 : 600,
      hidden: false,
      current: i === current,
      dim: i >= dimAt,
    };
  });
};

const Plot = ({ bars, isMobile, groupLabel }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const { max, ticks } = useMemo(
    () => chartScale(bars.map((b) => b.count)),
    [bars]
  );
  const at = (v) => `${(v / max) * AREA}px`;
  const tone = { faint: t.faint, sub: t.sub, primary: t.primary };

  return (
    <div style={{ overflowX: isMobile ? 'auto' : 'visible', marginTop: 18 }}>
      <div style={{ minWidth: isMobile ? 330 : 0 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <div
            aria-hidden="true"
            style={{
              position: 'relative',
              width: AXIS_WIDTH,
              height: PLOT_HEIGHT,
              flexShrink: 0,
            }}
          >
            {ticks.map((v) => (
              <span
                key={v}
                style={{
                  position: 'absolute',
                  right: 0,
                  bottom: at(v),
                  transform: 'translateY(50%)',
                  fontSize: 10,
                  lineHeight: 1,
                  color: t.faint,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {v.toLocaleString()}
              </span>
            ))}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ position: 'relative', height: PLOT_HEIGHT }}>
              {ticks.map((v) => (
                <div
                  key={v}
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: at(v),
                    height: 1,
                    background: v === 0 ? t.border : t.line,
                  }}
                />
              ))}
              <div
                role="group"
                aria-label={groupLabel}
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  display: 'flex',
                  alignItems: 'stretch',
                  gap: isMobile ? 3 : 5,
                }}
              >
                {bars.map((bar) => {
                  const pct = barPercent(bar.count, max);
                  return (
                    <Bar
                      key={bar.key}
                      $current={bar.current}
                      aria-label={`${bar.tipTitle}: ${bar.tipBody}`}
                      data-tip-title={bar.tipTitle}
                      data-tip={bar.tipBody}
                      data-tip-pin=""
                      data-dim={bar.dim ? '' : undefined}
                      style={bar.dim ? { opacity: 0.45 } : undefined}
                    >
                      <span
                        className="val"
                        aria-hidden="true"
                        style={{ color: bar.count ? t.sub : 'transparent' }}
                      >
                        {bar.count ? bar.count.toLocaleString() : ''}
                      </span>
                      <span
                        className="bar"
                        aria-hidden="true"
                        style={{
                          height: bar.count ? (pct / 100) * AREA : 2,
                          background: bar.count ? t.primary : inkA(theme, 0.14),
                        }}
                      />
                    </Bar>
                  );
                })}
              </div>
            </div>
            <div
              aria-hidden="true"
              style={{
                display: 'flex',
                gap: isMobile ? 3 : 5,
                marginTop: 8,
                paddingTop: 7,
                borderTop: '1px solid ' + t.line,
              }}
            >
              {bars.map((bar) => (
                <div
                  key={bar.key}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'center',
                    fontSize: isMobile ? 9.5 : 10.5,
                    fontWeight: bar.captionWeight,
                    color: tone[bar.captionTone],
                    whiteSpace: 'nowrap',
                    overflow: isMobile ? 'visible' : 'hidden',
                    visibility: bar.hidden ? 'hidden' : 'visible',
                    opacity: bar.dim ? 0.55 : 1,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {bar.caption}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Plot.propTypes = {
  bars: PropTypes.arrayOf(PropTypes.object).isRequired,
  isMobile: PropTypes.bool.isRequired,
  groupLabel: PropTypes.string.isRequired,
};

const EntriesChart = ({
  weekly,
  total,
  average,
  loading,
  isMobile,
  buckets,
  labels,
  dimFrom,
  currentIndex,
  title,
  summary,
  range,
  note,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const generic = Array.isArray(buckets);
  const hasWeeks = generic ? buckets.length > 0 : !!(weekly && weekly.length);
  const ready = !loading && hasWeeks;
  const weeks = hasWeeks && !generic ? weekly.length : 14;
  const bars = useMemo(
    () =>
      generic
        ? bucketBars(buckets, labels, { dimFrom, currentIndex })
        : weeklyBars(weekly || [], isMobile),
    [generic, buckets, labels, dimFrom, currentIndex, weekly, isMobile]
  );
  const summaryText = generic
    ? summary
    : `${entries(total)} in the last ${weeks} weeks · avg ${Number(
        average || 0
      ).toLocaleString(undefined, { maximumFractionDigits: 1 })}/week`;
  const rangeText = generic ? range : chartRange(weekly);
  const noteText = generic ? note : 'Weekly, ending today';

  return (
    <section
      aria-labelledby="fs-entries-chart-title"
      data-tour="submissions-chart"
      style={{
        background: t.card,
        border: '1px solid ' + t.border,
        borderRadius: 16,
        boxShadow: t.shadowCard,
        padding: isMobile ? '14px 14px 12px' : '18px 20px 14px',
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h2
            id="fs-entries-chart-title"
            style={{
              margin: 0,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              color: t.label,
            }}
          >
            {title}
          </h2>
          <div
            style={{
              marginTop: 4,
              fontSize: 13.5,
              fontWeight: 600,
              color: t.text,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {ready ? summaryText || ' ' : ' '}
          </div>
        </div>
        {ready ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 2,
              textAlign: 'right',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                fontSize: 12.5,
                fontWeight: 700,
                color: t.sub,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {rangeText}
            </span>
            {isMobile || !noteText ? null : (
              <span style={{ fontSize: 13, color: t.faint }}>{noteText}</span>
            )}
          </div>
        ) : null}
      </div>
      {ready ? (
        <Plot
          bars={bars}
          isMobile={isMobile}
          groupLabel={
            generic ? `${title}, in order` : 'Entries per week, oldest first'
          }
        />
      ) : null}
      {loading ? (
        <div style={{ marginTop: 18 }} aria-hidden="true">
          <SkeletonBar $w="100%" $h={`${PLOT_HEIGHT}px`} $r="10px" />
        </div>
      ) : null}
      {!loading && !hasWeeks ? (
        <div
          style={{
            padding: '40px 0 28px',
            textAlign: 'center',
            fontSize: 13.5,
            color: t.sub,
          }}
        >
          The chart couldn&apos;t be loaded. Refresh to try again.
        </div>
      ) : null}
    </section>
  );
};

EntriesChart.propTypes = {
  weekly: PropTypes.arrayOf(
    PropTypes.shape({
      week_start: PropTypes.string.isRequired,
      week_end: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    })
  ),
  total: PropTypes.number,
  average: PropTypes.number,
  loading: PropTypes.bool,
  isMobile: PropTypes.bool,
  // Generic mode (w9-plan §6.3 item 5); leave out for the weekly chart.
  buckets: PropTypes.arrayOf(PropTypes.number),
  labels: PropTypes.arrayOf(PropTypes.string),
  dimFrom: PropTypes.number,
  currentIndex: PropTypes.number,
  title: PropTypes.string,
  summary: PropTypes.string,
  range: PropTypes.string,
  note: PropTypes.string,
};

EntriesChart.defaultProps = {
  weekly: [],
  total: 0,
  average: 0,
  loading: false,
  isMobile: false,
  buckets: undefined,
  labels: undefined,
  dimFrom: null,
  currentIndex: null,
  title: 'Entries over time',
  summary: '',
  range: '',
  note: '',
};

export default EntriesChart;
